import { v4 as uuid } from 'uuid';
import { ContentBlockType, ImageBlock } from '../../model';
import type { ConverterContext } from '../blocks-to-tiptap.converter';
import type { BlockToTiptapConverter } from '../blocks-to-tiptap.converter';
import type { TiptapToBlockConverter } from '../tiptap-to-blocks.converter';

/**
 * Converts an ImageBlock to a TipTap image node.
 * Applies `context.transformImageSrc` to the src if provided.
 */
export const convertImageBlockToTiptap = ((block, context?: ConverterContext) => {
  const src = context?.transformImageSrc?.(block.src) ?? block.src;

  const attrs: Record<string, unknown> = {
    src,
    ...(block.alt && { alt: block.alt }),
    ...(block.title && { title: block.title }),
    ...(block.align && { 'data-align': block.align }),
    ...(block.width && { width: block.width }),
    ...(block.height && { height: block.height }),
  };

  if (block.caption) {
    attrs['showCaption'] = true;
    return {
      type: 'image',
      attrs,
      content: [{ type: 'text', text: block.caption }],
    };
  }

  return {
    type: 'image',
    attrs,
  };
}) satisfies BlockToTiptapConverter<ImageBlock>;

/**
 * Converts a TipTap image node to an ImageBlock.
 * Applies `context.transformImageSrc` to the src if provided.
 */
export const convertTiptapToImageBlock = ((json, context?) => {
  const attrs = json.attrs ?? {};

  const caption = json.content
    ?.map((node) => node.text ?? '')
    .join('')
    .trim();

  const rawSrc = attrs['src'] as string | undefined;
  const src = rawSrc ? (context?.transformImageSrc?.(rawSrc) ?? rawSrc) : undefined;

  const width = attrs['width'] ? Number(attrs['width']) : undefined;
  const height = attrs['height'] ? Number(attrs['height']) : undefined;

  return {
    id: uuid(),
    type: ContentBlockType.Image,
    src,
    ...(attrs['alt'] && { alt: attrs['alt'] as string }),
    ...(attrs['title'] && { title: attrs['title'] as string }),
    ...(attrs['data-align'] && { align: attrs['data-align'] as ImageBlock['align'] }),
    ...(caption && { caption }),
    ...(width && { width }),
    ...(height && { height }),
  };
}) satisfies TiptapToBlockConverter;
